import { useState, useEffect } from 'react';
import api from '../../api';

const VOLUME_PRESETS = [0.3, 0.5, 1.0, 1.5, 2.0];

export default function ContainerManager({ storeId, products, categories }) {
  const [containers, setContainers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editContainer, setEditContainer] = useState(null);
  const [form, setForm] = useState({ name:'', price:'', cost_price:'', stock_quantity:'0', low_stock_threshold:'' });
  const [error, setError] = useState('');

  // Прив'язки по категоріях
  const [catLinks, setCatLinks] = useState({}); // { category_id: [...links] }
  const [expandedCat, setExpandedCat] = useState(null);
  const [catLinkForm, setCatLinkForm] = useState({ container_product_id:'', volume:'' });
  const [catLinkError, setCatLinkError] = useState('');

  // Прив'язки по товарах (перевизначення)
  const [productLinks, setProductLinks] = useState({});
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [productLinkForm, setProductLinkForm] = useState({ container_product_id:'', volume:'' });
  const [productLinkError, setProductLinkError] = useState('');

  const [activeTab, setActiveTab] = useState('containers');

  useEffect(() => { loadContainers(); }, [storeId]);

  async function loadContainers() {
    const res = await api.get(`/containers/stores/${storeId}`).catch(() => ({ data: [] }));
    setContainers(res.data);
  }

  async function loadCatLinks(catId) {
    const res = await api.get(`/containers/category/${catId}/options`).catch(() => ({ data: [] }));
    setCatLinks(prev => ({ ...prev, [catId]: res.data }));
  }

  async function loadProductLinks(productId) {
    const res = await api.get(`/containers/product/${productId}/options`).catch(() => ({ data: [] }));
    setProductLinks(prev => ({ ...prev, [productId]: res.data }));
  }

  async function toggleCat(catId) {
    if (expandedCat === catId) { setExpandedCat(null); return; }
    setExpandedCat(catId);
    setCatLinkForm({ container_product_id:'', volume:'' });
    setCatLinkError('');
    await loadCatLinks(catId);
  }

  async function toggleProduct(productId) {
    if (expandedProduct === productId) { setExpandedProduct(null); return; }
    setExpandedProduct(productId);
    setProductLinkForm({ container_product_id:'', volume:'' });
    setProductLinkError('');
    await loadProductLinks(productId);
  }

  async function submitContainer(e) {
    e.preventDefault(); setError('');
    try {
      if (editContainer) {
        await api.patch(`/containers/stores/${storeId}/${editContainer.id}`, form);
      } else {
        await api.post(`/containers/stores/${storeId}`, form);
      }
      await loadContainers();
      setShowForm(false);
      setForm({ name:'', price:'', cost_price:'', stock_quantity:'0', low_stock_threshold:'' });
      setEditContainer(null);
    } catch(err) { setError(err.response?.data?.detail || 'Помилка'); }
  }

  async function deleteContainer(id) {
    if (!window.confirm('Видалити тару?')) return;
    await api.delete(`/containers/stores/${storeId}/${id}`);
    await loadContainers();
  }

  async function addCatLink(catId) {
    setCatLinkError('');
    try {
      await api.post(`/containers/category/${catId}/options`, {
        container_product_id: catLinkForm.container_product_id,
        volume: catLinkForm.volume,
      });
      await loadCatLinks(catId);
      setCatLinkForm({ container_product_id:'', volume:'' });
    } catch(err) { setCatLinkError(err.response?.data?.detail || 'Помилка'); }
  }

  async function removeCatLink(catId, linkId) {
    await api.delete(`/containers/category/${catId}/options/${linkId}`);
    await loadCatLinks(catId);
  }

  async function addProductLink(productId) {
    setProductLinkError('');
    try {
      await api.post(`/containers/product/${productId}/options`, {
        container_product_id: productLinkForm.container_product_id,
        volume: productLinkForm.volume,
      });
      await loadProductLinks(productId);
      setProductLinkForm({ container_product_id:'', volume:'' });
    } catch(err) { setProductLinkError(err.response?.data?.detail || 'Помилка'); }
  }

  async function removeProductLink(productId, linkId) {
    await api.delete(`/containers/product/${productId}/options/${linkId}`);
    await loadProductLinks(productId);
  }

  function openEdit(c) {
    setEditContainer(c);
    setForm({ name:c.name, price:c.price, cost_price:c.cost_price,
      stock_quantity:c.stock_quantity, low_stock_threshold:c.low_stock_threshold||'' });
    setShowForm(true); setError('');
  }

  // Тільки літрові та вагові товари (не тара)
  const productsWithContainers = products.filter(p =>
    (p.unit === 'liter' || p.unit === 'gram') && !p.is_container
  );

  // Категорії які мають літрові/вагові товари
  const relevantCats = categories.filter(c =>
    productsWithContainers.some(p => p.category_id === c.id)
  );

  function renderLinkForm(linkForm, setLinkForm, onAdd, linkError, scope) {
    return (
      <div style={styles.addLinkForm}>
        <div style={styles.volumeRow}>
          <span style={styles.smallLabel}>Об'єм:</span>
          {VOLUME_PRESETS.map(v => (
            <button key={v} type="button"
              style={{...styles.presetBtn, ...(parseFloat(linkForm.volume)===v ? styles.presetBtnActive : {})}}
              onClick={() => setLinkForm(f => ({...f, volume:String(v)}))}>
              {v}л
            </button>
          ))}
          <input style={styles.volumeInput} type="number" step="0.1" placeholder="інший"
            value={!VOLUME_PRESETS.map(String).includes(String(linkForm.volume)) && linkForm.volume ? linkForm.volume : ''}
            onChange={e => setLinkForm(f => ({...f, volume:e.target.value}))} />
        </div>
        <div style={styles.addLinkRow}>
          <select style={styles.containerSelect}
            value={linkForm.container_product_id}
            onChange={e => setLinkForm(f => ({...f, container_product_id:e.target.value}))}>
            <option value="">Оберіть тару...</option>
            {containers.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} — {parseFloat(c.price).toFixed(2)} ₴
                (залишок: {parseFloat(c.stock_quantity).toFixed(0)})
              </option>
            ))}
          </select>
          <button style={styles.addLinkBtn}
            onClick={onAdd}
            disabled={!linkForm.volume || !linkForm.container_product_id}>
            + Додати
          </button>
        </div>
        {linkError && <p style={styles.error}>{linkError}</p>}
      </div>
    );
  }

  function renderLinks(links, onRemove) {
    if (!links || links.length === 0) return null;
    return (
      <div style={styles.linkOptions}>
        {links.map(link => (
          <div key={link.id} style={styles.linkOption}>
            <span style={styles.linkVolume}>{parseFloat(link.volume).toFixed(1)} л</span>
            <span style={styles.linkArrow}>→</span>
            <span style={styles.linkName}>{link.container.name}</span>
            <span style={styles.linkPrice}>{parseFloat(link.container.price).toFixed(2)} ₴</span>
            {link.container.stock_quantity != null && (
              <span style={styles.linkStock}>
                {parseFloat(link.container.stock_quantity).toFixed(0)} шт
              </span>
            )}
            <button style={styles.unlinkBtn} onClick={() => onRemove(link.id)}>✕</button>
          </div>
        ))}
      </div>
    );
  }

  const tabs = [
    { key:'containers', label:'🍶 Тара' },
    { key:'categories', label:'📂 По категоріях' },
    { key:'products', label:'📦 По товарах' },
  ];

  return (
    <div style={styles.wrap}>
      <div style={styles.tabBar}>
        {tabs.map(t => (
          <button key={t.key}
            style={activeTab===t.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ТАРА ── */}
      {activeTab === 'containers' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>🍶 Управління тарою</h3>
            <button style={styles.addBtn} onClick={() => {
              setShowForm(true); setEditContainer(null);
              setForm({ name:'', price:'', cost_price:'', stock_quantity:'0', low_stock_threshold:'' });
              setError('');
            }}>+ Додати</button>
          </div>

          {showForm && (
            <div style={styles.form}>
              <form onSubmit={submitContainer}>
                <div style={styles.formRow}>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Назва</label>
                    <input style={styles.input} placeholder="Пляшка 0.5л"
                      value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} required />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Ціна (₴)</label>
                    <input style={styles.input} type="number" step="0.01"
                      value={form.price} onChange={e => setForm(f=>({...f,price:e.target.value}))} required />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Собівартість (₴)</label>
                    <input style={styles.input} type="number" step="0.01"
                      value={form.cost_price} onChange={e => setForm(f=>({...f,cost_price:e.target.value}))} />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Залишок (шт)</label>
                    <input style={styles.input} type="number"
                      value={form.stock_quantity} onChange={e => setForm(f=>({...f,stock_quantity:e.target.value}))} />
                  </div>
                  <div style={styles.fieldGroup}>
                    <label style={styles.label}>Поріг залишку</label>
                    <input style={styles.input} type="number" placeholder="—"
                      value={form.low_stock_threshold} onChange={e => setForm(f=>({...f,low_stock_threshold:e.target.value}))} />
                  </div>
                </div>
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.formBtns}>
                  <button type="submit" style={styles.saveBtn}>{editContainer ? 'Зберегти' : 'Створити'}</button>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowForm(false)}>Скасувати</button>
                </div>
              </form>
            </div>
          )}

          {containers.length === 0 ? (
            <p style={styles.empty}>Тари ще немає</p>
          ) : (
            <div style={styles.containerList}>
              {containers.map(c => {
                const isLow = c.low_stock_threshold && parseFloat(c.stock_quantity) <= parseFloat(c.low_stock_threshold);
                const isOut = parseFloat(c.stock_quantity) === 0;
                return (
                  <div key={c.id} style={styles.containerItem}>
                    <div style={styles.containerIcon}>🍶</div>
                    <div style={styles.containerInfo}>
                      <div style={styles.containerName}>{c.name}</div>
                      <div style={styles.containerMeta}>
                        <span style={{color:'#f5a623'}}>{parseFloat(c.price).toFixed(2)} ₴</span>
                        &nbsp;·&nbsp;
                        <span style={{color: isOut?'#ff6b6b':isLow?'#f5a623':'#5fbf8f'}}>
                          {parseFloat(c.stock_quantity).toFixed(0)} шт
                          {isOut&&' 🔴'}{isLow&&!isOut&&' ⚠️'}
                        </span>
                      </div>
                    </div>
                    <div style={styles.containerActions}>
                      <button style={styles.editBtn} onClick={() => openEdit(c)}>✏️</button>
                      <button style={styles.deleteBtn} onClick={() => deleteContainer(c.id)}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── ПО КАТЕГОРІЯХ ── */}
      {activeTab === 'categories' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📂 Тара по категоріях</h3>
          <p style={styles.hint}>
            Налаштуйте варіанти об'єму для категорії — всі товари цієї категорії автоматично отримають ці варіанти в POS касі
          </p>

          {containers.length === 0 && (
            <div style={styles.warningBox}>
              ⚠️ Спочатку додайте тару на вкладці "🍶 Тара"
            </div>
          )}

          {relevantCats.length === 0 ? (
            <p style={styles.empty}>Немає категорій з літровими або ваговими товарами</p>
          ) : relevantCats.map(cat => {
            const links = catLinks[cat.id] || [];
            const isExpanded = expandedCat === cat.id;
            const catProducts = productsWithContainers.filter(p => p.category_id === cat.id);
            return (
              <div key={cat.id} style={styles.expandItem}>
                <div style={styles.expandHeader} onClick={() => toggleCat(cat.id)}>
                  <div style={styles.expandInfo}>
                    <span style={styles.expandIcon}>
                      {cat.photo_url && !cat.photo_url.startsWith('http') ? cat.photo_url : '📂'}
                    </span>
                    <span style={styles.expandName}>{cat.name}</span>
                    <span style={styles.expandMeta}>{catProducts.length} товарів</span>
                    {links.length > 0 && (
                      <span style={styles.linkCount}>✓ {links.length} варіант(и)</span>
                    )}
                  </div>
                  <span style={styles.arrow}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {isExpanded && (
                  <div style={styles.expandBody}>
                    {renderLinks(links, (linkId) => removeCatLink(cat.id, linkId))}
                    {containers.length > 0
                      ? renderLinkForm(catLinkForm, setCatLinkForm, () => addCatLink(cat.id), catLinkError, 'category')
                      : <p style={styles.noContainers}>Спочатку додайте тару ↑</p>
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── ПО ТОВАРАХ (ПЕРЕВИЗНАЧЕННЯ) ── */}
      {activeTab === 'products' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>📦 Індивідуальні налаштування товарів</h3>
          <p style={styles.hint}>
            Якщо товар має особливі варіанти тари — налаштуйте тут. Це перевизначить налаштування категорії для цього товару.
          </p>

          {productsWithContainers.length === 0 ? (
            <p style={styles.empty}>Немає літрових або вагових товарів</p>
          ) : productsWithContainers.map(product => {
            const links = productLinks[product.id] || [];
            const isExpanded = expandedProduct === product.id;
            const cat = categories.find(c => c.id === product.category_id);
            const catHasLinks = cat && catLinks[cat.id]?.length > 0;

            return (
              <div key={product.id} style={styles.expandItem}>
                <div style={styles.expandHeader} onClick={() => toggleProduct(product.id)}>
                  <div style={styles.expandInfo}>
                    <span style={styles.expandName}>{product.name}</span>
                    <span style={styles.expandMeta}>
                      {product.unit === 'liter' ? '🍺 л' : '⚖️ кг'}
                      {cat && ` · ${cat.name}`}
                    </span>
                    {links.length > 0 && (
                      <span style={styles.linkCountOverride}>⚡ {links.length} перевизначень</span>
                    )}
                    {links.length === 0 && catHasLinks && (
                      <span style={styles.inheritedBadge}>📂 з категорії</span>
                    )}
                  </div>
                  <span style={styles.arrow}>{isExpanded ? '▲' : '▼'}</span>
                </div>

                {isExpanded && (
                  <div style={styles.expandBody}>
                    {links.length === 0 && catHasLinks && (
                      <div style={styles.inheritedInfo}>
                        ℹ️ Цей товар використовує варіанти з категорії "{cat.name}".
                        Додайте нижче щоб перевизначити.
                      </div>
                    )}
                    {renderLinks(links, (linkId) => removeProductLink(product.id, linkId))}
                    {containers.length > 0
                      ? renderLinkForm(productLinkForm, setProductLinkForm, () => addProductLink(product.id), productLinkError, 'product')
                      : <p style={styles.noContainers}>Спочатку додайте тару ↑</p>
                    }
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { marginBottom:'24px' },
  tabBar: { display:'flex', gap:'0', marginBottom:'0', borderBottom:'1px solid #38301f' },
  tab: { padding:'10px 20px', background:'transparent', color:'#9a8a6e', border:'none', borderBottom:'2px solid transparent', cursor:'pointer', fontSize:'13px' },
  tabActive: { padding:'10px 20px', background:'transparent', color:'#f5a623', border:'none', borderBottom:'2px solid #f5a623', cursor:'pointer', fontSize:'13px', fontWeight:'bold' },
  section: { background:'#211c14', border:'1px solid #38301f', borderRadius:'0 0 12px 12px', padding:'20px' },
  sectionHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px' },
  sectionTitle: { color:'#f5a623', margin:0, fontSize:'15px' },
  addBtn: { padding:'6px 14px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' },
  hint: { color:'#9a8a6e', fontSize:'13px', marginBottom:'14px', marginTop:0 },
  warningBox: { background:'#3d2800', border:'1px solid #f5a623', color:'#f5a623', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', fontSize:'13px' },
  form: { background:'#1c1812', borderRadius:'8px', padding:'14px', marginBottom:'14px', border:'1px solid #38301f' },
  formRow: { display:'flex', gap:'10px', flexWrap:'wrap', marginBottom:'8px' },
  fieldGroup: { flex:1, minWidth:'120px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'10px', marginBottom:'4px', textTransform:'uppercase' },
  smallLabel: { color:'#9a8a6e', fontSize:'12px', whiteSpace:'nowrap' },
  input: { width:'100%', padding:'7px 10px', borderRadius:'6px', border:'1px solid #38301f', background:'#211c14', color:'#fff', fontSize:'13px', boxSizing:'border-box' },
  error: { color:'#ff6b6b', fontSize:'12px', margin:'6px 0 0' },
  formBtns: { display:'flex', gap:'8px' },
  saveBtn: { padding:'7px 18px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' },
  cancelBtn: { padding:'7px 12px', background:'transparent', color:'#9a8a6e', border:'1px solid #38301f', borderRadius:'6px', cursor:'pointer', fontSize:'12px' },
  empty: { color:'#9a8a6e', textAlign:'center', padding:'12px', fontSize:'13px' },
  containerList: { display:'flex', flexDirection:'column', gap:'6px' },
  containerItem: { background:'#1c1812', borderRadius:'8px', padding:'10px 14px', display:'flex', alignItems:'center', gap:'10px', border:'1px solid #38301f' },
  containerIcon: { fontSize:'18px', flexShrink:0 },
  containerInfo: { flex:1 },
  containerName: { fontWeight:'bold', fontSize:'13px', marginBottom:'2px' },
  containerMeta: { color:'#9a8a6e', fontSize:'12px' },
  containerActions: { display:'flex', gap:'5px' },
  editBtn: { background:'#38301f', border:'none', borderRadius:'5px', padding:'4px 7px', cursor:'pointer', fontSize:'12px' },
  deleteBtn: { background:'rgba(255,107,107,.18)', border:'none', borderRadius:'5px', padding:'4px 7px', cursor:'pointer', fontSize:'12px' },
  expandItem: { background:'#1c1812', borderRadius:'8px', border:'1px solid #38301f', overflow:'hidden', marginBottom:'6px' },
  expandHeader: { padding:'12px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' },
  expandInfo: { display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap' },
  expandIcon: { fontSize:'18px' },
  expandName: { fontWeight:'bold', fontSize:'14px' },
  expandMeta: { color:'#9a8a6e', fontSize:'12px' },
  linkCount: { background:'#2d6a4f', color:'#5fbf8f', padding:'2px 8px', borderRadius:'10px', fontSize:'11px' },
  linkCountOverride: { background:'#3d2800', color:'#f5a623', padding:'2px 8px', borderRadius:'10px', fontSize:'11px' },
  inheritedBadge: { background:'#38301f', color:'#6cb6ff', padding:'2px 8px', borderRadius:'10px', fontSize:'11px' },
  arrow: { color:'#9a8a6e', fontSize:'12px', flexShrink:0 },
  expandBody: { padding:'12px 16px', borderTop:'1px solid #38301f', background:'#211c14' },
  inheritedInfo: { color:'#6cb6ff', fontSize:'12px', marginBottom:'10px', background:'#0f2744', padding:'8px 10px', borderRadius:'6px' },
  linkOptions: { display:'flex', flexDirection:'column', gap:'5px', marginBottom:'10px' },
  linkOption: { display:'flex', alignItems:'center', gap:'8px', padding:'6px 10px', background:'#1c1812', borderRadius:'6px', flexWrap:'wrap' },
  linkVolume: { color:'#f5a623', fontWeight:'bold', fontSize:'13px', minWidth:'35px' },
  linkArrow: { color:'#9a8a6e' },
  linkName: { flex:1, fontSize:'13px' },
  linkPrice: { color:'#f5a623', fontSize:'12px' },
  linkStock: { color:'#9a8a6e', fontSize:'11px' },
  unlinkBtn: { background:'transparent', border:'none', color:'#9a8a6e', cursor:'pointer', fontSize:'13px', padding:'0 3px' },
  addLinkForm: { borderTop:'1px solid #38301f', paddingTop:'10px', marginTop:'4px' },
  volumeRow: { display:'flex', alignItems:'center', gap:'5px', marginBottom:'8px', flexWrap:'wrap' },
  presetBtn: { padding:'4px 9px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'5px', cursor:'pointer', fontSize:'12px' },
  presetBtnActive: { background:'#f5a623', color:'#14110c', fontWeight:'bold' },
  volumeInput: { width:'65px', padding:'4px 8px', borderRadius:'5px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'12px' },
  addLinkRow: { display:'flex', gap:'8px' },
  containerSelect: { flex:1, padding:'7px 10px', borderRadius:'6px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'12px' },
  addLinkBtn: { padding:'7px 14px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight:'bold', fontSize:'12px' },
  noContainers: { color:'#f5a623', fontSize:'12px', textAlign:'center' },
};
